import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ListItem } from '../../molecules';
import './PluginListItem.css';

export type PluginListAction = {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  tone?: 'primary' | 'neutral' | 'success' | 'warning' | 'danger';
  disabled?: boolean;
};

export type PluginListItemProps = {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  badges?: ReactNode;
  onClick?: () => void;
  active?: boolean;
  className?: string;
  actions?: PluginListAction[];
};

const SWIPE_THRESHOLD = 44;

export function PluginListItem({
  leading,
  title,
  subtitle,
  meta,
  badges,
  onClick,
  active,
  className,
  actions = [],
}: PluginListItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const lastDeltaX = useRef(0);
  const lastDeltaY = useRef(0);
  const actionsWidth = actions.length > 0 ? Math.max(actions.length * 72, 72) : 0;

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
    touchStartY.current = event.touches[0]?.clientY ?? null;
    lastDeltaX.current = 0;
    lastDeltaY.current = 0;
  }, []);

  const handleTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const currentX = event.touches[0]?.clientX ?? touchStartX.current;
    const currentY = event.touches[0]?.clientY ?? touchStartY.current;
    const deltaX = currentX - touchStartX.current;
    const deltaY = currentY - touchStartY.current;
    lastDeltaX.current = deltaX;
    lastDeltaY.current = deltaY;

    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      if (isOpen) {
        setIsOpen(false);
      }
      return;
    }

    if (deltaX <= -SWIPE_THRESHOLD) {
      setIsOpen(true);
    } else if (deltaX >= SWIPE_THRESHOLD) {
      setIsOpen(false);
    }
  }, [isOpen]);

  const handleTouchEnd = useCallback(() => {
    if (isOpen && Math.abs(lastDeltaX.current) < SWIPE_THRESHOLD && Math.abs(lastDeltaY.current) < SWIPE_THRESHOLD) {
      setIsOpen(false);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  }, []);

  const handleActionClick = (action: PluginListAction) => {
    if (action.disabled) return;
    action.onClick();
    setIsOpen(false);
  };

  return (
    <div
      className={`plugin-swipe ${isOpen ? 'plugin-swipe--open' : ''} ${className ?? ''}`}
      style={{ ['--plugin-swipe-width' as string]: `${actionsWidth}px` }}
    >
      {actions.length > 0 && (
        <div className="plugin-swipe__actions" aria-hidden={!isOpen}>
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`plugin-swipe__action plugin-swipe__action--${action.tone ?? 'neutral'}`}
              onClick={() => handleActionClick(action)}
              disabled={action.disabled}
            >
              {action.icon && <span className="plugin-swipe__icon">{action.icon}</span>}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
      <div
        className="plugin-swipe__content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <ListItem
          leading={leading}
          title={title}
          subtitle={subtitle}
          meta={meta}
          badges={badges}
          onClick={onClick}
          active={active}
          className="list-item--compact"
        />
      </div>
    </div>
  );
}

export default PluginListItem;
